import { ApolloError } from "@apollo/client";
import { stateHasIndependentGeneratorStatus } from "encamp-shared/src/waste/stateGeneratorStatusCodes";
import {
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
  UpsertSubmissionWasteActivityInput,
} from "generated-graphql/graphql";

import { safeToastMessage } from "../../ReadOnly/safeToastMessage";

/**
 * Pure selection/drift/payload logic for the editable Waste Activity step.
 * Kept free of React/Apollo component code so prefill, override
 * detection, and save-payload assembly are unit-testable.
 *
 * Model (docs/plans/2026-07-07-submission-waste-activity-persistence-design.md):
 * - `saved` is the `Submission.wasteActivity` row — null until the user first
 *   saves the step. It carries the chosen values AND the recommendation shown
 *   at that save.
 * - The "live recommendation" is what the step's queries compute this render
 *   (calculated generator status, reportable waste-code aggregation).
 * - Effective ("chosen") value per field: the user's in-session edit if any,
 *   else the saved row's chosen value, else the live recommendation.
 * - Every save writes all eight fields: chosen + the live recommendation the
 *   user was just shown.
 */

/** The four editable fields, as saved on `SubmissionWasteActivity`. */
export type SavedWasteActivity = {
  federalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  stateGeneratorStatus?: StateRegulatoryStatus | null;
  federalWasteCodes: string[];
  stateWasteCodes: string[];
  recommendedFederalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  recommendedStateGeneratorStatus?: StateRegulatoryStatus | null;
  recommendedFederalWasteCodes: string[];
  recommendedStateWasteCodes: string[];
};

/** Live recommendation for the generator-status half (from the status queries). */
export type GeneratorStatusRecommendation = {
  federalGeneratorStatus: RcraInfoFederalWasteGeneratorStatus | null;
  stateGeneratorStatus: StateRegulatoryStatus | null;
};

/** Live recommendation for the waste-codes half (reportable aggregation). */
export type WasteCodesRecommendation = {
  federalWasteCodes: string[];
  stateWasteCodes: string[];
};

export type WasteActivityRecommendation = GeneratorStatusRecommendation & WasteCodesRecommendation;

/**
 * In-session, unsaved user edits. A key is present only once the user touched
 * that field; `undefined` therefore means "not edited" (fall through to
 * saved/recommended), while an explicit `null` is a real "no status" edit.
 */
export type WasteActivityEdits = {
  federalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  stateGeneratorStatus?: StateRegulatoryStatus | null;
  federalWasteCodes?: string[];
  stateWasteCodes?: string[];
};

/**
 * Effective value of one field: in-session edit → saved chosen value → live
 * recommendation. `hasSaved` (row existence) decides the fallback tier because
 * a saved row's value may legitimately be null and must NOT fall through to
 * the recommendation — row existence means "the user saved this".
 */
export function resolveChosenField<T>(
  edit: T | undefined,
  savedValue: T | null,
  hasSaved: boolean,
  recommended: T | null
): T | null {
  if (edit !== undefined) {
    return edit;
  }
  return hasSaved ? savedValue : recommended;
}

export type FieldDrift = {
  /** The chosen value differs from the recommendation it was made against. */
  overridden: boolean;
  /** A row was saved and the live recommendation has since moved off the stored one. */
  recommendationChanged: boolean;
};

/**
 * Override/drift indicator inputs for one field. The override reference is the
 * STORED recommendation when a row exists (that's what the user accepted or
 * overrode), else the live one.
 */
export function computeFieldDrift<T>({
  chosen,
  savedRecommended,
  liveRecommended,
  hasSaved,
  equals = (a, b) => a === b,
  isEmpty = (value) => value == null,
}: {
  chosen: T | null;
  savedRecommended: T | null;
  liveRecommended: T | null;
  hasSaved: boolean;
  equals?: (a: T | null, b: T | null) => boolean;
  /**
   * "There is no recommendation" test for the field's value type: null for
   * statuses (default), null-or-empty for code lists (`wasteCodesEmpty`).
   */
  isEmpty?: (value: T | null) => boolean;
}): FieldDrift {
  const reference = hasSaved ? savedRecommended : liveRecommended;
  return {
    // A chosen value cannot "differ from" a recommendation that doesn't exist,
    // so an absent reference never flags an override.
    overridden: !isEmpty(reference) && !equals(chosen, reference),
    // Deliberately NOT gated on emptiness: a recommendation appearing since
    // the save (stored none → live LQG) or disappearing (stored LQG → live
    // none) IS a change worth surfacing, unlike the override caption above.
    recommendationChanged: hasSaved && !equals(savedRecommended, liveRecommended),
  };
}

/** "There is no recommendation" test for code lists: null or empty. */
export function wasteCodesEmpty(codes: readonly string[] | null): boolean {
  return codes == null || codes.length === 0;
}

/** Order-insensitive equality for waste-code lists (codes are kept normalized). */
export function wasteCodesEqual(a: readonly string[] | null, b: readonly string[] | null): boolean {
  const aSet = new Set(a ?? []);
  const bSet = new Set(b ?? []);
  if (aSet.size !== bSet.size) {
    return false;
  }
  for (const code of aSet) {
    if (!bSet.has(code)) {
      return false;
    }
  }
  return true;
}

/**
 * Client-side normalization of freeSolo waste-code entries: trim, uppercase,
 * drop empties, dedupe — matching the server's `normalizeWasteCodes` so what
 * the user sees is what gets persisted.
 */
export function normalizeWasteCodeInput(values: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const value of values) {
    const normalized = value.trim().toUpperCase();
    if (normalized.length > 0) {
      seen.add(normalized);
    }
  }
  return [...seen];
}

/**
 * Assembles the full 8-field upsert payload: effective chosen values plus the
 * live recommendation the user was shown this render (captured client-side per
 * the design doc — NOT a server-side recompute).
 */
export function buildUpsertInput({
  submissionId,
  saved,
  edits,
  recommendation,
}: {
  submissionId: string;
  saved: SavedWasteActivity | null;
  edits: WasteActivityEdits;
  recommendation: WasteActivityRecommendation;
}): UpsertSubmissionWasteActivityInput {
  const hasSaved = saved != null;
  return {
    submissionId,
    federalGeneratorStatus: resolveChosenField(
      edits.federalGeneratorStatus,
      saved?.federalGeneratorStatus ?? null,
      hasSaved,
      recommendation.federalGeneratorStatus
    ),
    stateGeneratorStatus: resolveChosenField(
      edits.stateGeneratorStatus,
      saved?.stateGeneratorStatus ?? null,
      hasSaved,
      recommendation.stateGeneratorStatus
    ),
    federalWasteCodes:
      resolveChosenField(
        edits.federalWasteCodes,
        saved?.federalWasteCodes ?? null,
        hasSaved,
        recommendation.federalWasteCodes
      ) ?? [],
    stateWasteCodes:
      resolveChosenField(
        edits.stateWasteCodes,
        saved?.stateWasteCodes ?? null,
        hasSaved,
        recommendation.stateWasteCodes
      ) ?? [],
    recommendedFederalGeneratorStatus: recommendation.federalGeneratorStatus,
    recommendedStateGeneratorStatus: recommendation.stateGeneratorStatus,
    recommendedFederalWasteCodes: recommendation.federalWasteCodes,
    recommendedStateWasteCodes: recommendation.stateWasteCodes,
  };
}

// ---------------------------------------------------------------------------
// Generator-status recommendation sources
// ---------------------------------------------------------------------------

/**
 * Federal recommendation: the window's highest calculated status. The charts
 * query and the submission row now share the single `RcraInfoFederalWasteGeneratorStatus`
 * GraphQL enum, so this only normalizes `undefined` → `null`.
 */
export function mapCalculatedFederalStatus(
  status: RcraInfoFederalWasteGeneratorStatus | null | undefined
): RcraInfoFederalWasteGeneratorStatus | null {
  return status ?? null;
}

/**
 * State ranking mirroring `stateGeneratorStatusRank` in
 * packages/encamp-shared/src/dataloaders/facilityRegulatoryStatus/generatorStatus.ts
 * (not importable here: that module pulls in the server Prisma client). Keep in
 * sync; the Record type keeps it exhaustive against the GraphQL enum.
 */
const STATE_GENERATOR_STATUS_RANK: Record<StateRegulatoryStatus, number> = {
  [StateRegulatoryStatus.LargeQuantityGenerator]: 9,
  [StateRegulatoryStatus.MarylandGeneratorFullyRegulated]: 8,
  [StateRegulatoryStatus.MediumQuantityGenerator]: 7,
  [StateRegulatoryStatus.SmallQuantityGenerator]: 6,
  [StateRegulatoryStatus.VerySmallQuantityGenerator]: 5,
  [StateRegulatoryStatus.KansasSmallQuantityGenerator]: 4,
  [StateRegulatoryStatus.TexasClass_1OnlyGenerator]: 3,
  [StateRegulatoryStatus.ConditionallyExemptSmallQuantityGenerator]: 2,
  [StateRegulatoryStatus.MiniQuantityGenerator]: 1,
  [StateRegulatoryStatus.SameAsFederal]: 0,
  [StateRegulatoryStatus.NotAGenerator]: 0,
  [StateRegulatoryStatus.Undetermined]: 0,
  [StateRegulatoryStatus.Pending]: 0,
};

/**
 * State recommendation: highest-ranked calculated state status over the
 * window's months, mirroring the snapshot fallback
 * (`resolveGeneratorStatusFromCalculatedStatuses` in encamp-snapshot):
 * CalculatedRegulatoryStatus-derived (NOT the EPA-reported status), gated on
 * the state actually having an independent state-status program. `>=` prefers
 * the most recent month among equal ranks (input is month-ascending),
 * matching the resolver's most-recent-wins tiebreak.
 */
export function highestCalculatedStateStatus(
  monthStatuses: ReadonlyArray<StateRegulatoryStatus | null | undefined>,
  state: string | null | undefined
): StateRegulatoryStatus | null {
  if (!stateHasIndependentGeneratorStatus(state)) {
    return null;
  }
  let highest: StateRegulatoryStatus | null = null;
  for (const status of monthStatuses) {
    if (status == null) {
      continue;
    }
    if (
      highest == null ||
      STATE_GENERATOR_STATUS_RANK[status] >= STATE_GENERATOR_STATUS_RANK[highest]
    ) {
      highest = status;
    }
  }
  return highest;
}

// ---------------------------------------------------------------------------
// Labels + option parsing for the selects
// ---------------------------------------------------------------------------

export const FEDERAL_STATUS_OPTIONS: RcraInfoFederalWasteGeneratorStatus[] = Object.values(
  RcraInfoFederalWasteGeneratorStatus
);

export const STATE_STATUS_OPTIONS: StateRegulatoryStatus[] = Object.values(StateRegulatoryStatus);

/**
 * State affinity per StateRegulatoryStatus member: statuses that exist only in
 * one state's program map to that state's code; `null` marks generic/universal
 * statuses offered everywhere. Derived from where each value is produced or
 * accepted — the per-state RCRAInfo code lists in
 * packages/encamp-shared/src/waste/stateGeneratorStatusCodes.ts and the
 * per-state calculators in
 * apps/tasks/process-manifests/src/calculateGeneratorStatus/calculators/states/.
 * The shared code lists alone can't be the filter: e.g. Kansas's RCRAInfo code
 * list omits KANSAS_SMALL_QUANTITY_GENERATOR even though Encamp's KS calculator
 * recommends it. Keep in sync with those sources; the Record type keeps this
 * exhaustive against the GraphQL enum.
 */
const STATE_STATUS_AFFINITY: Record<StateRegulatoryStatus, string | null> = {
  [StateRegulatoryStatus.KansasSmallQuantityGenerator]: "KS",
  [StateRegulatoryStatus.MarylandGeneratorFullyRegulated]: "MD",
  [StateRegulatoryStatus.MiniQuantityGenerator]: "MN",
  [StateRegulatoryStatus.TexasClass_1OnlyGenerator]: "TX",
  [StateRegulatoryStatus.MediumQuantityGenerator]: "WA",
  // Generic: CESQG is the pre-2016 federal term still used by several states'
  // programs (FL, ID, KS, NE, PA per the shared RCRAInfo code lists).
  [StateRegulatoryStatus.ConditionallyExemptSmallQuantityGenerator]: null,
  [StateRegulatoryStatus.LargeQuantityGenerator]: null,
  [StateRegulatoryStatus.SmallQuantityGenerator]: null,
  [StateRegulatoryStatus.VerySmallQuantityGenerator]: null,
  [StateRegulatoryStatus.SameAsFederal]: null,
  [StateRegulatoryStatus.NotAGenerator]: null,
  [StateRegulatoryStatus.Undetermined]: null,
  [StateRegulatoryStatus.Pending]: null,
};

/**
 * Options for the state generator status select, filtered to the facility's
 * state: generic statuses (no state affinity) are always offered, and
 * state-specific ones only for their own state. A state without an independent
 * state-status program therefore gets just the generic values — consistent
 * with `highestCalculatedStateStatus`, which never recommends a state-specific
 * status for such states — rather than a disabled select, since RCRAInfo still
 * accepts generic statuses (e.g. Same as Federal) there.
 *
 * `chosen` is the select's current value (edit → saved → recommendation): when
 * it falls outside the filtered list (legacy or out-of-state data) it is kept
 * as an option so the select never silently blanks a persisted choice.
 */
export function applicableStateStatusOptions(
  state: string | null | undefined,
  chosen: StateRegulatoryStatus | null
): StateRegulatoryStatus[] {
  const normalizedState = state == null ? null : state.trim().toUpperCase();
  return STATE_STATUS_OPTIONS.filter((status) => {
    const affinity = STATE_STATUS_AFFINITY[status];
    return affinity === null || affinity === normalizedState || status === chosen;
  });
}

/** Narrow a Select's string value back to the enum without casting. */
export function parseFederalStatus(value: string): RcraInfoFederalWasteGeneratorStatus | null {
  return FEDERAL_STATUS_OPTIONS.find((status) => status === value) ?? null;
}

/** Narrow a Select's string value back to the enum without casting. */
export function parseStateStatus(value: string): StateRegulatoryStatus | null {
  return STATE_STATUS_OPTIONS.find((status) => status === value) ?? null;
}

// ---------------------------------------------------------------------------
// Save-error shaping
// ---------------------------------------------------------------------------

/**
 * Shape an `upsertSubmissionWasteActivity` failure into a toast message.
 * BAD_USER_INPUT errors surface their (allowlisted, user-safe) message — the
 * frozen-submission rejection. Compliance-engine code issues never reject the
 * save; they surface live via `submissionWasteActivityInputValidator` while
 * the user edits instead.
 */
export function describeWasteActivitySaveError(error: unknown): string {
  const fallback = "Error saving Waste Activity.";
  if (!(error instanceof ApolloError)) {
    return fallback;
  }
  const gqlError = error.graphQLErrors.find((e) => e.extensions?.code === "BAD_USER_INPUT");
  if (!gqlError) {
    return fallback;
  }
  return safeToastMessage(gqlError.message) ?? fallback;
}
